import { useLocation } from 'react-router';

import {
  Box,
  Dialog,
  Button,
  MenuItem,
  DialogTitle,
  ListSubheader,
  DialogContent,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import { AuthLoader } from 'src/pages/auth/components/shared/AuthLoader';
import { useModulePermissions } from 'src/pages/auth/hooks/useModulePermissions';
import { AccountAddEditForm } from 'src/pages/accounts/components/account-add-edit-form';
import { MultiSelectAutocomplete } from 'src/pages/tickets/components/custom-multiSelect';

import { Form, Field } from 'src/components/hook-form';
import { FieldsRenderer } from 'src/components/field-renderer/field-renderer';
import SearchSelectRecordField from 'src/components/search-field/select-record-dialog';

import { useContactForm } from '../hooks/use-contact-form';
import { contactFormConfig } from '../config/contact-form.config';

import type { IContact } from '../contacts.types';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: () => void;
  selectedUser?: IContact;
  handleSubmitData: (data: any, assignedPayload: any) => void;
  isCallPopup?: boolean;
  isLoader?: boolean;
  customFields: any[] | [];
};

export function ContactAddEditForm({
  open,
  onClose,
  selectedUser,
  handleSubmitData,
  isCallPopup = false,
  isLoader = false,
  customFields,
}: Props) {
  const { t } = useTranslate('translation');
  const location = useLocation();

  // Permissions
  const { isView: isAccountView, isCreate: isAccountCreate } = useModulePermissions('company');
  const { loginApiData } = useAppSelector((state) => (state as any).auth);

  const noPermission = new Set(['account']);
  const hasAccountPathPermission = location.pathname
    .split('/')
    .some((item) => noPermission.has(item));

  const {
    formKey,
    methods,
    onSubmit,
    handleClose,
    stages,
    accountMenuDialog,
    selectedAccount,
    selectedUserAccDtl,
    setSelectedUserAccDtl,
    handleCreateAccount,
    handleCreateAccSubmit,
    assigneeProps,
  } = useContactForm({
    open,
    onClose,
    selectedUser,
    handleSubmitData,
    customFields,
  });

  const title = `${!isCallPopup && selectedUser?.firstName !== '' ? t('EDIT') : t('CREATE')} ${t('CONTACT')}`;

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiPaper-rounded': {
            p: 0.5,
            boxShadow: '0px 0px 30px rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <DialogTitle sx={[(theme) => ({ p: theme.spacing(3, 3, 2, 3) })]}>{title}</DialogTitle>

        <DialogContent dividers sx={{ pt: 1, pb: 0, border: 'none' }}>
          {isLoader && <AuthLoader />}

          <Form key={formKey} methods={methods} onSubmit={onSubmit}>
            <Box>
              {contactFormConfig.map((section, index) => (
                <Box key={index} sx={{ mb: index === contactFormConfig.length - 1 ? 0 : 3 }}>
                  <FieldsRenderer
                    fields={section.fields
                      .filter((field) => {
                        // Filter lifecycle stage based on tenant config
                        if (field.name === 'lifecycleStage' && !loginApiData?.lifecycleStatus) {
                          return false;
                        }
                        return true;
                      })
                      .map((field) => {
                        // Special components mapping
                        if (field.name === 'assignUsers') {
                          return {
                            ...field,
                            component: MultiSelectAutocomplete,
                            componentProps: {
                              ...assigneeProps,
                              isLoader,
                              placeholder: `+ ${t('RECIPIENTS')}`,
                              isOptionEqualToValue: (a: any, b: any) => a.id === b.id,
                            },
                          };
                        }

                        if (field.name === 'companyId') {
                          return {
                            ...field,
                            component: SearchSelectRecordField,
                            componentProps: {
                              type: 'Account',
                              disabled: hasAccountPathPermission || !isAccountView,
                              label: t(field.label),
                              setSelectedUserAccDtl,
                              defaultValue: selectedUserAccDtl?.value,
                              setValue: methods.setValue,
                              onAddBtnClick: handleCreateAccount,
                              isAccountCreate: !isAccountCreate,
                              isAddButton: true,
                              placeholder: t('SEARCH_ACCOUNT'),
                            },
                          };
                        }

                        // For lifecycleStage, let's use the standard select but with special options transform
                        if (field.name === 'lifecycleStage') {
                          return {
                            ...field,
                            type: 'custom',
                            component: () => (
                              <Box sx={{ width: '100%' }}>
                                <Field.Select
                                  name={field.name}
                                  label={t(field.label)}
                                  slotProps={{ inputLabel: { shrink: true } }}
                                  fullWidth
                                >
                                  {Object.entries(
                                    stages.reduce((acc: Record<string, any[]>, option: any) => {
                                      const type = option.type || 'Other';
                                      acc[type] = acc[type] || [];
                                      acc[type].push(option);
                                      return acc;
                                    }, {})
                                  ).flatMap(([type, items]) => [
                                    <ListSubheader
                                      key={`header-${type}`}
                                      disableSticky
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: 13,
                                        lineHeight: '32px',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      {t(type)} {t('STAGES')}
                                    </ListSubheader>,

                                    ...items.map((option: any) => (
                                      <MenuItem key={option.id} value={option.id} sx={{ pl: 3 }}>
                                        {t(option.name)}
                                      </MenuItem>
                                    )),
                                  ])}
                                </Field.Select>
                              </Box>
                            ),
                          };
                        }

                        return field;
                      })}
                    control={methods.control}
                    useGrid
                  />
                </Box>
              ))}
            </Box>

            {/* Dynamic Custom Fields Section */}
            {customFields && customFields.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <FieldsRenderer
                  fields={customFields.map((f) => ({
                    name: f.name || f.id,
                    label: f.label,
                    type: 'customField',
                    componentProps: { fieldData: f },
                    gridSize: { xs: 12, md: 12 },
                  }))}
                  control={methods.control}
                  useGrid
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'end', gap: 2, my: 3 }}>
              <Button variant="outlined" color="inherit" onClick={handleClose}>
                {t('CANCEL')}
              </Button>
              <Button type="submit" variant="contained">
                {t('SAVE')}
              </Button>
            </Box>
          </Form>
        </DialogContent>
      </Dialog>

      {accountMenuDialog.value && (
        <AccountAddEditForm
          open={accountMenuDialog.value}
          onClose={() => {
            accountMenuDialog.onFalse();
          }}
          handleSubmitData={handleCreateAccSubmit}
          selectedUser={selectedAccount}
          customFields={[]}
        />
      )}
    </>
  );
}
